/**
 * useTrainingTracker — background knowledge training for the agent-creation
 * flow. Knowledge is ingested asynchronously (pages scraped, documents read,
 * connector files imported). Steps hand us the knowledge_ids they started
 * ("track") and we turn their progress into per-kind jobs the page renders as
 * strips — so the owner can keep going while training runs.
 *
 * Two sources keep the numbers live and accurate:
 *   • WebSocket push: one { knowledge_id, status } per item (fast path;
 *     reconnects with backoff when the socket drops).
 *   • Status poll: GET onboarding/knowledge_status?bot_id&ids= every few
 *     seconds while anything is pending (source of truth; catches every
 *     message the socket missed — reconnects, worker fan-out, reloads).
 * Tracked ids + totals are persisted per agent in sessionStorage, so a page
 * reload restores the strips and the poll settles them.
 *
 * jobs: { [kind]: { total, done, phase: 'training' | 'done' } }
 * `onAllDone` fires once every tracked item has reported (completed or failed).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { disposeWebSocket } from '../../utils/disposeWebSocket';

const DONE_HIDE_MS = 5000;   // a finished strip stays visible this long
const POLL_MS = 3000;
const API = process.env.REACT_APP_API_BASE_URL;
const memKey = (botId) => `ac:train:${botId}`;

export default function useTrainingTracker({ botId, userId, onAllDone }) {
  const [jobs, setJobs] = useState({});
  const idsRef = useRef(new Map());   // knowledge_id -> kind (pending only)
  const jobsRef = useRef({}); jobsRef.current = jobs;
  const timersRef = useRef({});
  const doneCbRef = useRef(onAllDone);
  doneCbRef.current = onAllDone;
  const [pendingCount, setPendingCount] = useState(0);

  const persist = useCallback(() => {
    if (!botId) return;
    try {
      const pending = Array.from(idsRef.current.entries());
      if (!pending.length) sessionStorage.removeItem(memKey(botId));
      else sessionStorage.setItem(memKey(botId), JSON.stringify({ ids: pending, jobs: jobsRef.current }));
    } catch { /* ignore */ }
  }, [botId]);

  // Restore an in-flight batch after a reload (the poll settles it right away).
  useEffect(() => {
    if (!botId) return;
    try {
      const raw = sessionStorage.getItem(memKey(botId));
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!Array.isArray(saved?.ids) || !saved.ids.length) return;
      idsRef.current = new Map(saved.ids.map(([k, v]) => [String(k), v]));
      const restored = {};
      Object.entries(saved.jobs || {}).forEach(([kind, j]) => { if (j && j.phase !== 'done') restored[kind] = { ...j, phase: 'training' }; });
      setJobs(restored);
      setPendingCount(idsRef.current.size);
    } catch { /* ignore */ }
  }, [botId]);

  const track = useCallback((ids, kind) => {
    const list = (ids || []).filter((x) => x != null);
    if (!list.length) return;
    list.forEach((id) => idsRef.current.set(String(id), kind));
    setJobs((j) => {
      const cur = j[kind] || { total: 0, done: 0, phase: 'training' };
      if (timersRef.current[kind]) { clearTimeout(timersRef.current[kind]); timersRef.current[kind] = null; }
      // A finished strip still on screen restarts as a fresh bar for the new batch.
      const base = cur.phase === 'done' ? { total: 0, done: 0 } : cur;
      const next = { ...j, [kind]: { total: base.total + list.length, done: base.done, phase: 'training' } };
      jobsRef.current = next;
      return next;
    });
    setPendingCount(idsRef.current.size);
    persist();
  }, [persist]);

  // One tracked item finished (from either source). Idempotent per id.
  const settle = useCallback((key) => {
    const kind = idsRef.current.get(String(key));
    if (!kind) return;
    idsRef.current.delete(String(key));
    const allIn = idsRef.current.size === 0;
    setJobs((j) => {
      const cur = j[kind];
      if (!cur) return j;
      const done = Math.min(cur.total, cur.done + 1);
      const finished = done >= cur.total;
      if (finished) {
        if (timersRef.current[kind]) clearTimeout(timersRef.current[kind]);
        timersRef.current[kind] = setTimeout(() => {
          setJobs((x) => { const n = { ...x }; delete n[kind]; jobsRef.current = n; return n; });
        }, DONE_HIDE_MS);
      }
      const next = { ...j, [kind]: { ...cur, done, phase: finished ? 'done' : 'training' } };
      jobsRef.current = next;
      return next;
    });
    setPendingCount(idsRef.current.size);
    persist();
    if (allIn) setTimeout(() => doneCbRef.current?.(), 0);
  }, [persist]);

  // ── WebSocket (fast path), reconnecting ──
  useEffect(() => {
    if (!botId || !userId) return undefined;
    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    if (!raw) return undefined;
    let ws = null; let closed = false; let retry = 0; let timer = null;
    const connect = () => {
      if (closed) return;
      try { ws = new WebSocket(raw.replace(/^http/, 'ws')); } catch (e) { console.error('training WS init failed', e); return; }
      ws.addEventListener('open', () => {
        if (closed) return;
        retry = 0;
        ws.send(JSON.stringify({ type: 'init', user_id: userId, token: localStorage.getItem('token') }));
      });
      ws.addEventListener('message', (ev) => {
        if (closed) return;
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.knowledge_id == null) return;
        if (msg.status === 'completed' || msg.status === 'failed') settle(msg.knowledge_id);
      });
      ws.addEventListener('close', () => {
        if (closed) return;
        timer = setTimeout(connect, Math.min(10000, 1000 * 2 ** retry++));
      });
    };
    connect();
    return () => { closed = true; if (timer) clearTimeout(timer); disposeWebSocket(ws); };
  }, [botId, userId, settle]);

  // ── Status poll (source of truth) while anything is pending ──
  useEffect(() => {
    if (!botId || pendingCount === 0) return undefined;
    let stop = false; let timer = null;
    const tick = async () => {
      if (stop) return;
      const ids = Array.from(idsRef.current.keys());
      if (!ids.length) return;
      try {
        for (let i = 0; i < ids.length && !stop; i += 100) {
          const chunk = ids.slice(i, i + 100);
          const r = await fetch(`${API}onboarding/knowledge_status?bot_id=${encodeURIComponent(botId)}&ids=${chunk.join(',')}`);
          if (!r.ok) continue;
          const j = await r.json().catch(() => ({}));
          (j.items || []).forEach((it) => { if (it.status === 'completed' || it.status === 'failed') settle(it.id); });
        }
      } catch { /* try again next tick */ }
      if (!stop && idsRef.current.size) timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, 1500);
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [botId, pendingCount, settle]);

  useEffect(() => () => { Object.values(timersRef.current).forEach((x) => x && clearTimeout(x)); }, []);

  return { jobs, track };
}
